import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FiMail, FiLock, FiSun, FiMoon, FiArrowLeft, FiKey } from 'react-icons/fi';
import { useTheme } from '../contexts/ThemeContext';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import { authAPI } from '../api/endpoints';
import { validateEmail, validatePassword } from '../utils/helpers';
import { showToast } from '../components/common/Toaster';
import logo from '../assets/images/logo.png';
import {
  LoginContainer,
  LoginCard,
  LoginLeft,
  LoginRight,
  Logo,
  LoginTitle,
  LoginSubtitle,
  LoginForm,
  SignupPrompt,
  ThemeToggle,
  BackgroundShape,
  FeatureList,
  FeatureItem,
  FeatureIcon,
  BackButton,
} from './Login.styles';

const ResetPassword = () => {
  const { isDarkMode, toggleTheme } = useTheme();
  const [searchParams] = useSearchParams();
  
  const [formData, setFormData] = useState({
    email: searchParams.get('email') || '',
    reset_token: searchParams.get('token') || '',
    new_password: '',
    confirm_password: '',
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!formData.reset_token) {
      newErrors.reset_token = 'Reset code is required';
    } else if (formData.reset_token.length < 6) {
      newErrors.reset_token = 'Reset code must be at least 6 characters';
    }
    
    if (!formData.new_password) {
      newErrors.new_password = 'New password is required';
    } else if (!validatePassword(formData.new_password)) {
      newErrors.new_password = 'Password must be at least 8 characters with uppercase, lowercase, and number';
    }
    
    if (!formData.confirm_password) {
      newErrors.confirm_password = 'Please confirm your password';
    } else if (formData.new_password !== formData.confirm_password) {
      newErrors.confirm_password = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    setLoading(true);
    
    try {
      await authAPI.passwordResetConfirm(
        formData.email,
        formData.reset_token,
        formData.new_password
      );
      
      setIsSuccess(true);
      showToast('success', 'Password reset successful! Redirecting to login...', 3000);
      
      // Redirect to login after delay
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
    } catch (error) {
      setErrors({
        general: error.response?.data?.error || 'Password reset failed. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <LoginContainer>
        <BackgroundShape className="shape-1" />
        <BackgroundShape className="shape-2" />
        <BackgroundShape className="shape-3" />
        
        <ThemeToggle onClick={toggleTheme}>
          {isDarkMode ? <FiSun /> : <FiMoon />}
        </ThemeToggle>
        
        <LoginCard>
          <LoginLeft>
            <Logo style={{ justifyContent: 'center' }}>
              <img src={logo} alt="Dira Learners Logo" style={{ width: '200px', height: 'auto' }} />
            </Logo>
            
            <FeatureList>
              <FeatureItem>
                <FeatureIcon>🔐</FeatureIcon>
                <div>
                  <h4>Password Updated</h4>
                  <p>Your password has been successfully reset</p>
                </div>
              </FeatureItem>
              
              <FeatureItem>
                <FeatureIcon>✅</FeatureIcon>
                <div>
                  <h4>Secure Account</h4>
                  <p>Your account is now protected with a new password</p>
                </div>
              </FeatureItem>
              
              <FeatureItem>
                <FeatureIcon>🚀</FeatureIcon>
                <div>
                  <h4>Ready to Go</h4>
                  <p>You can now sign in with your new password</p>
                </div>
              </FeatureItem>
            </FeatureList>
          </LoginLeft>
          
          <LoginRight>
            <div style={{ textAlign: 'center' }}>
              <div style={{ 
                width: '80px', 
                height: '80px', 
                background: 'linear-gradient(135deg, #10B981, #059669)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
                fontSize: '32px'
              }}>
                ✓
              </div>
              
              <LoginTitle>Password Reset Complete!</LoginTitle>
              <LoginSubtitle>
                Your password has been successfully updated. You can now sign in with your new password.
              </LoginSubtitle>
              
              <div style={{ 
                background: '#F0FDF4',
                border: '1px solid #BBF7D0',
                borderRadius: '12px',
                padding: '16px',
                margin: '24px 0',
                color: '#166534'
              }}>
                <p style={{ margin: 0, fontSize: '14px' }}>
                  Redirecting you to the login page in a moment...
                </p>
              </div>
              
              <Link to="/login">
                <Button variant="primary" size="large">
                  Go to Login
                </Button>
              </Link>
            </div>
          </LoginRight>
        </LoginCard>
      </LoginContainer>
    );
  }

  return (
    <LoginContainer>
      <BackgroundShape className="shape-1" />
      <BackgroundShape className="shape-2" />
      <BackgroundShape className="shape-3" />
      
      <ThemeToggle onClick={toggleTheme}>
        {isDarkMode ? <FiSun /> : <FiMoon />}
      </ThemeToggle>
      
      <LoginCard>
        <LoginLeft>
          <Logo style={{ justifyContent: 'center' }}>
            <img src={logo} alt="Dira Learners Logo" style={{ width: '200px', height: 'auto' }} />
          </Logo>
          
          <FeatureList>
            <FeatureItem>
              <FeatureIcon>🔑</FeatureIcon>
              <div>
                <h4>Secure Reset</h4>
                <p>Enter the code from your email to reset your password</p>
              </div>
            </FeatureItem>
            
            <FeatureItem>
              <FeatureIcon>⚡</FeatureIcon>
              <div>
                <h4>Quick Process</h4>
                <p>Reset your password in just a few simple steps</p>
              </div>
            </FeatureItem>
            
            <FeatureItem>
              <FeatureIcon>🔒</FeatureIcon>
              <div>
                <h4>Strong Password</h4>
                <p>Create a secure password to protect your account</p>
              </div>
            </FeatureItem>
          </FeatureList>
        </LoginLeft>
        
        <LoginRight>
          <BackButton to="/forgot-password">
            <FiArrowLeft />
            Back to Forgot Password
          </BackButton>
          
          <LoginTitle>Reset Your Password</LoginTitle>
          <LoginSubtitle>Enter the code from your email and create a new password</LoginSubtitle>
          
          {errors.general && (
            <div style={{ 
              padding: '12px', 
              backgroundColor: '#FEE2E2', 
              color: '#DC2626', 
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '14px'
            }}>
              {errors.general}
            </div>
          )}
          
          <LoginForm onSubmit={handleSubmit}>
            <Input
              label="Email Address"
              type="email"
              name="email"
              placeholder="Enter your email address"
              value={formData.email}
              onChange={handleChange}
              error={errors.email}
              leftIcon={<FiMail />}
              required
            />
            
            <Input
              label="Reset Code"
              type="text"
              name="reset_token"
              placeholder="Enter the 6-digit code from your email"
              value={formData.reset_token}
              onChange={handleChange}
              error={errors.reset_token}
              leftIcon={<FiKey />}
              required
            />
            
            <Input
              label="New Password"
              type="password"
              name="new_password"
              placeholder="Create a new password"
              value={formData.new_password}
              onChange={handleChange}
              error={errors.new_password}
              leftIcon={<FiLock />}
              required
            />
            
            <Input
              label="Confirm New Password"
              type="password"
              name="confirm_password"
              placeholder="Confirm your new password"
              value={formData.confirm_password}
              onChange={handleChange}
              error={errors.confirm_password}
              leftIcon={<FiLock />}
              required
            />
            
            <Button
              type="submit"
              variant="primary"
              size="large"
              fullWidth
              loading={loading}
            >
              Reset Password
            </Button>
          </LoginForm>
          
          <SignupPrompt>
            Remember your password? <Link to="/login">Sign in</Link>
          </SignupPrompt>
        </LoginRight>
      </LoginCard>
    </LoginContainer>
  );
};

export default ResetPassword;
